import { supabase } from '../supabaseClient/supabaseClient';

/**
 * User Memory Service
 * Manages long-term facts about the user across all components
 * Categories: profile, preferences, academic, productivity
 */
class UserMemoryService {
  constructor() {
    this.cache = new Map(); // In-memory cache for fast access
    this.initialized = false;
  }

  /**
   * Initialize user memory - load from database and localStorage
   * @param {string} userId
   */
  async initialize(userId) {
    if (this.initialized) return;

    try {
      // Try to load from localStorage first (faster)
      const cached = this.loadFromLocalStorage(userId);
      if (cached) {
        this.cache = new Map(Object.entries(cached));
      }

      // Fetch from database (source of truth)
      const { data, error } = await supabase
        .from('ml_user_memory')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      // Update cache with database data
      if (data) {
        data.forEach((fact) => {
          const key = `${fact.category}:${fact.fact_key}`;
          this.cache.set(key, {
            value: fact.fact_value,
            source: fact.source,
            updatedAt: fact.updated_at,
          });
        });

        // Save to localStorage
        this.saveToLocalStorage(userId);
      }

      this.initialized = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error initializing:', error);
    }
  }

  /**
   * Get a specific memory fact
   * @param {string} category - 'profile', 'preferences', 'academic', 'productivity'
   * @param {string} key - Fact key
   */
  get(category, key) {
    const cacheKey = `${category}:${key}`;
    return this.cache.get(cacheKey)?.value || null;
  }

  /**
   * Get all facts in a category
   * @param {string} category
   */
  getCategory(category) {
    const facts = {};
    this.cache.forEach((value, key) => {
      if (key.startsWith(`${category}:`)) {
        const factKey = key.split(':')[1];
        facts[factKey] = value.value;
      }
    });
    return facts;
  }

  /**
   * Get all memory facts (all categories)
   */
  getAll() {
    const allFacts = {
      profile: this.getCategory('profile'),
      preferences: this.getCategory('preferences'),
      academic: this.getCategory('academic'),
      productivity: this.getCategory('productivity'),
    };
    return allFacts;
  }

  /**
   * Set a memory fact
   * @param {string} userId
   * @param {string} category
   * @param {string} key
   * @param {any} value
   * @param {string} source - 'explicit', 'inferred', 'imported'
   */
  async set(userId, category, key, value, source = 'explicit') {
    const cacheKey = `${category}:${key}`;

    // Update cache
    this.cache.set(cacheKey, {
      value,
      source,
      updatedAt: new Date().toISOString(),
    });

    // Save to localStorage
    this.saveToLocalStorage(userId);

    // Persist to database
    try {
      const { error } = await supabase.from('ml_user_memory').upsert({
        user_id: userId,
        category,
        fact_key: key,
        fact_value: value,
        source,
        updated_at: new Date().toISOString(),
      });

      if (error) throw error;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error setting fact:', error);
    }
  }

  /**
   * Delete a memory fact
   * @param {string} userId
   * @param {string} category
   * @param {string} key
   */
  async delete(userId, category, key) {
    const cacheKey = `${category}:${key}`;

    // Remove from cache
    this.cache.delete(cacheKey);

    // Save to localStorage
    this.saveToLocalStorage(userId);

    // Delete from database
    try {
      const { error } = await supabase
        .from('ml_user_memory')
        .delete()
        .eq('user_id', userId)
        .eq('category', category)
        .eq('fact_key', key);

      if (error) throw error;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error deleting fact:', error);
    }
  }

  /**
   * Import facts from user profile
   * @param {string} userId
   * @param {object} profile - User profile data
   */
  async importFromProfile(userId, profile) {
    const facts = [];

    // Profile facts
    if (profile.full_name) {
      facts.push({ category: 'profile', key: 'name', value: profile.full_name });
    }
    if (profile.email) {
      facts.push({ category: 'profile', key: 'email', value: profile.email });
    }
    if (profile.bio) {
      facts.push({ category: 'profile', key: 'bio', value: profile.bio });
    }
    if (profile.timezone) {
      facts.push({ category: 'profile', key: 'timezone', value: profile.timezone });
    }

    // Preferences
    if (profile.learning_style) {
      facts.push({ category: 'preferences', key: 'learning_style', value: profile.learning_style });
    }
    if (profile.preferred_language) {
      facts.push({
        category: 'preferences',
        key: 'preferred_language',
        value: profile.preferred_language,
      });
    }

    // Academic
    if (profile.education_level) {
      facts.push({ category: 'academic', key: 'education_level', value: profile.education_level });
    }
    if (profile.areas_of_interest) {
      facts.push({
        category: 'academic',
        key: 'areas_of_interest',
        value: profile.areas_of_interest,
      });
    }
    if (profile.learning_goals) {
      facts.push({ category: 'academic', key: 'learning_goals', value: profile.learning_goals });
    }

    // Productivity
    if (profile.work_schedule) {
      facts.push({ category: 'productivity', key: 'work_schedule', value: profile.work_schedule });
    }
    if (profile.peak_hours) {
      facts.push({ category: 'productivity', key: 'peak_hours', value: profile.peak_hours });
    }

    // Batch insert
    for (const fact of facts) {
      await this.set(userId, fact.category, fact.key, fact.value, 'imported');
    }
  }

  /**
   * Format memory facts for LLM context
   * Returns a structured string representation
   */
  formatForContext() {
    const allFacts = this.getAll();
    const sections = [];

    // Profile section
    if (Object.keys(allFacts.profile).length > 0) {
      sections.push('**Profile:**');
      Object.entries(allFacts.profile).forEach(([key, value]) => {
        sections.push(`- ${this.formatKey(key)}: ${this.formatValue(value)}`);
      });
    }

    // Preferences section
    if (Object.keys(allFacts.preferences).length > 0) {
      sections.push('\n**Preferences:**');
      Object.entries(allFacts.preferences).forEach(([key, value]) => {
        sections.push(`- ${this.formatKey(key)}: ${this.formatValue(value)}`);
      });
    }

    // Academic section
    if (Object.keys(allFacts.academic).length > 0) {
      sections.push('\n**Academic Background:**');
      Object.entries(allFacts.academic).forEach(([key, value]) => {
        sections.push(`- ${this.formatKey(key)}: ${this.formatValue(value)}`);
      });
    }

    // Productivity section
    if (Object.keys(allFacts.productivity).length > 0) {
      sections.push('\n**Productivity Patterns:**');
      Object.entries(allFacts.productivity).forEach(([key, value]) => {
        sections.push(`- ${this.formatKey(key)}: ${this.formatValue(value)}`);
      });
    }

    return sections.join('\n');
  }

  /**
   * Helper: Format key for display
   */
  formatKey(key) {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Helper: Format value for display
   */
  formatValue(value) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * Save cache to localStorage
   */
  saveToLocalStorage(userId) {
    try {
      const cacheObj = {};
      this.cache.forEach((value, key) => {
        cacheObj[key] = value;
      });
      localStorage.setItem(`user_memory_${userId}`, JSON.stringify(cacheObj));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error saving to localStorage:', error);
    }
  }

  /**
   * Load cache from localStorage
   */
  loadFromLocalStorage(userId) {
    try {
      const cached = localStorage.getItem(`user_memory_${userId}`);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error loading from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear all memory (use with caution)
   */
  async clearAll(userId) {
    this.cache.clear();
    localStorage.removeItem(`user_memory_${userId}`);

    try {
      await supabase.from('ml_user_memory').delete().eq('user_id', userId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UserMemoryService] Error clearing memory:', error);
    }
  }
}

// Singleton instance
const userMemoryService = new UserMemoryService();

export default userMemoryService;
